import { useRef, useEffect } from 'react';
import Globe from 'react-globe.gl';
import { SatellitePoint, GroundTrack } from '../types/satellite';

interface SatelliteGlobeProps {
  globePoints: SatellitePoint[];
  groundTracks: GroundTrack[];
  onSatelliteClick?: (satellite: SatellitePoint) => void;
  height?: number;
}

export function SatelliteGlobe({ 
  globePoints, 
  groundTracks, 
  onSatelliteClick,
  height = window.innerHeight - 120 
}: SatelliteGlobeProps) {
  
  const globeRef = useRef<any>();
  
  // Auto-rotate globe for better visualization
  useEffect(() => {
    if (globeRef.current) {
      // Smooth rotation
      const controls = globeRef.current.controls();
      if (controls) {
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.5; // Very slow rotation
        controls.enableDamping = true;
        controls.dampingFactor = 0.1;
      }
    }
  }, []);
  
  const handlePointClick = (point: any) => {
    if (onSatelliteClick) {
      onSatelliteClick(point);
    }
    
    // Focus camera on clicked satellite
    if (globeRef.current) {
      globeRef.current.pointOfView({
        lat: point.lat,
        lng: point.lng,
        altitude: Math.max(2.5, point.alt * 6371 / 1000 + 1.5) // Adjust based on satellite altitude
      }, 1000);
    }
  };
  
  return (
    <div className="flex-1 relative">
      <Globe
        ref={globeRef}
        height={height}
        
        // Globe appearance
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
        backgroundColor="#0b0f1a"
        atmosphereColor="#4c9eff"
        atmosphereAltitude={0.15}
        
        // Satellite points
        pointsData={globePoints}
        pointLabel={(d: any) => `
          <div style="color: white; background: rgba(0,0,0,0.8); padding: 8px; border-radius: 4px; font-size: 12px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${d.name}</div>
            <div>Lat: ${d.lat.toFixed(3)}°</div>
            <div>Lng: ${d.lng.toFixed(3)}°</div>
            <div>Alt: ${(d.alt * 6371).toFixed(1)} km</div>
            <div style="margin-top: 4px; font-size: 10px; opacity: 0.8;">Click for details</div>
          </div>
        `}
        pointRadius={(d: any) => d.size}
        pointColor={(d: any) => d.color}
        pointAltitude={(d: any) => d.alt}
        onPointClick={handlePointClick}
        pointResolution={8} // Smooth point rendering
        
        // Ground tracks
        pathsData={groundTracks}
        pathPoints={(d: any) => d.path}
        pathPointLat={(p: any) => p[0]}
        pathPointLng={(p: any) => p[1]}
        pathPointAlt={() => 0.002} // Slight elevation for visibility
        pathColor={() => "rgba(255,255,255,0.3)"}
        pathStroke={1.5}
        pathTransitionDuration={0}
        
        // Performance optimizations
        rendererConfig={{
          antialias: true,
          alpha: true,
        }}
        
        // Controls
        enablePointerInteraction={true}
        
        // Initial view - removed pointOfView due to type issues
      />
      
      {/* Overlay information */}
      <div className="absolute top-4 right-4 bg-black/60 text-white p-3 rounded-lg text-sm">
        <div className="font-semibold mb-2">Satellite Status</div>
        <div>Active: {globePoints.length}</div>
        {groundTracks.length > 0 && (
          <div>Tracks: {groundTracks.length}</div>
        )}
        <div className="mt-2 text-xs opacity-70">
          Click satellites for details
        </div>
      </div>
      
      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-black/60 text-white p-3 rounded-lg text-xs">
        <div className="font-semibold mb-2">Altitude Legend</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-400"></div>
            <span>&lt; 500 km (VLEO)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-400"></div>
            <span>500-1000 km (LEO)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-400"></div>
            <span>1K-20K km (MEO)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <span>&gt; 20K km (GEO)</span>
          </div>
        </div>
      </div>
    </div>
  );
}